import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, FileUp, RefreshCw, Sparkles, Trash2, UploadCloud, X } from "lucide-react";
import { adminApi, errText } from "./adminApi";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HW = "/hw";
const CATS = [["", "All"], ["class_6", "Class 6"], ["class_7", "Class 7"], ["class_8", "Class 8"], ["class_9", "Class 9"], ["class_10", "Class 10"], ["class_11", "Class 11"], ["class_12", "Class 12"], ["jee_main", "JEE Main"], ["jee_advanced", "JEE Advanced"], ["neet", "NEET UG"]];
const QTYPES = ["mcq", "numerical", "conceptual", "formula_based", "assertion_reason", "multi_statement", "short_answer", "long_answer", "diagram_based"];
const fmtTs = (ts) => ts ? new Date(ts * 1000).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "—";
const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: paise % 100 ? 2 : 0 })}`;

/* ---------- shared bits ---------- */
function useSyllabusOptions(category) {
  const [chapters, setChapters] = useState([]);
  useEffect(() => {
    if (!category) { setChapters([]); return; }
    adminApi.get(`${HW}/admin/syllabus`, { params: { category } }).then(({ data }) => {
      const map = {};
      data.syllabus.forEach((s) => { map[s.chapter] = map[s.chapter] || []; map[s.chapter].push(s.topic); });
      setChapters(Object.entries(map).map(([chapter, topics]) => ({ chapter, topics })));
    }).catch(() => setChapters([]));
  }, [category]);
  return chapters;
}

function CatChapterTopic({ value, onChange, prefix }) {
  const chapters = useSyllabusOptions(value.category);
  const chapter = chapters.find((c) => c.chapter === value.chapter);
  return (
    <>
      <select className="admin-input" value={value.category} onChange={(e) => onChange({ category: e.target.value, chapter: "", topic: "" })} data-testid={`${prefix}-category`}>
        <option value="">Class/Exam…</option>
        {CATS.filter(([k]) => k).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <select className="admin-input" value={value.chapter} onChange={(e) => onChange({ ...value, chapter: e.target.value, topic: "" })} data-testid={`${prefix}-chapter`}>
        <option value="">Chapter…</option>
        {chapters.map((c) => <option key={c.chapter}>{c.chapter}</option>)}
      </select>
      <select className="admin-input" value={value.topic} onChange={(e) => onChange({ ...value, topic: e.target.value })} data-testid={`${prefix}-topic`}>
        <option value="">Topic…</option>
        {(chapter?.topics || []).map((t) => <option key={t}>{t}</option>)}
      </select>
    </>
  );
}

/* ---------- Dashboard ---------- */
function HwDash() {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminApi.get(`${HW}/admin/stats`).then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return <div className="admin-empty">Loading…</div>;
  const cells = [
    ["Registered students", stats.total_students], ["Active subscriptions", stats.active_subscriptions],
    ["Expired subscriptions", stats.expired_subscriptions], ["Active this week", stats.active_students],
    ["Assignments", stats.total_assignments], ["Submissions", stats.total_submissions],
    ["Pending evaluations", stats.pending_evaluations], ["Results published", stats.evaluated_submissions],
    ["Avg score", `${stats.avg_percentage}%`],
    ["Question bank", `${stats.bank_approved} approved / ${stats.bank_total}`],
    ["Awaiting review", stats.pending_review],
    ["PDF jobs", stats.pdf_processing ? `${stats.pdf_processing} processing` : stats.pdf_failed ? `${stats.pdf_failed} failed` : "idle"],
    ["AI usage this month", `${stats.ai_calls_month} calls`],
    ["Est. AI cost", `₹${stats.ai_cost_estimate_rupees}`],
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }} data-testid="hw-admin-stats">
      {cells.map(([label, val]) => (
        <div className="admin-card" key={label} style={{ textAlign: "center" }}>
          <b style={{ fontSize: 22, color: "#eef4fc" }}>{val}</b>
          <p className="lead-sub" style={{ margin: 0 }}>{label}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------- Students ---------- */
function HwStudents() {
  const [students, setStudents] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [history, setHistory] = useState(null);
  const [comment, setComment] = useState("");
  const [notice, setNotice] = useState("");
  const [sheet, setSheet] = useState(null);  // {student_id, name, html, note}
  useEffect(() => { adminApi.get(`${HW}/admin/students`).then(({ data }) => setStudents(data.students)).catch(() => setStudents([])); }, []);

  const openSheet = async (s) => {
    setSheet({ student_id: s.student_id, name: s.name, html: "", note: "Loading preview…" });
    try {
      const { data } = await adminApi.get(`${HW}/admin/sunday-revision/preview/${s.student_id}`);
      setSheet({ student_id: s.student_id, name: data.student_name, html: data.html, note: data.note });
    } catch (e) { setSheet({ student_id: s.student_id, name: s.name, html: "", note: errText(e) }); }
  };

  const openHistory = async (sid) => {
    if (openId === sid) { setOpenId(null); return; }
    setOpenId(sid);
    setHistory(null);
    setNotice("");
    const { data } = await adminApi.get(`${HW}/admin/students/${sid}/history`);
    setHistory(data);
    setComment(data.teacher_comment || "");
  };
  const saveComment = async () => {
    await adminApi.post(`${HW}/admin/students/${openId}/report-comment`, { comment });
    setNotice("Report comment saved — the student sees it on their 30-day report.");
  };

  if (!students) return <div className="admin-empty">Loading…</div>;
  return (
    <div className="admin-card" data-testid="hw-admin-students">
      {sheet && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.8)", zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
             data-testid="hw-sheet-modal" onClick={() => setSheet(null)}>
          <div style={{ background: "#fff", borderRadius: 12, maxWidth: 640, width: "100%", maxHeight: "84vh", overflow: "auto", padding: 4 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="admin-row" style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", justifyContent: "space-between" }}>
              <b style={{ color: "#0b2545", fontSize: 13 }}>Sunday Revision Sheet — {sheet.name} (email preview)</b>
              <button className="admin-btn" onClick={() => setSheet(null)} data-testid="hw-sheet-close"><X size={13} /></button>
            </div>
            <div style={{ padding: 14, color: "#1e293b" }} data-testid="hw-sheet-body">
              {sheet.html
                ? <div dangerouslySetInnerHTML={{ __html: sheet.html }} />
                : <p style={{ color: "#64748b", fontSize: 13 }}>{sheet.note}</p>}
            </div>
          </div>
        </div>
      )}
      {students.length === 0 && <div className="admin-empty">No homework students yet.</div>}
      {students.map((s, i) => (
        <div key={s.student_id} style={{ borderTop: i ? "1px solid #22314a" : "none", padding: "8px 0" }} data-testid={`hw-student-${i}`}>
          <div className="admin-row">
            <span className={`status-pill ${s.program?.active ? "approved" : "locked"}`}>{s.program?.active ? "PAID" : "FREE"}</span>
            <span style={{ flex: 1, color: "#eef4fc" }}>{s.name} <small className="lead-sub">{s.email}</small></span>
            <span className="lead-sub">{s.category_label}</span>
            <span className="lead-sub">{s.assignments} hw{s.program ? ` · quota ${s.program.quota_used}/${s.program.quota_total}` : ""}</span>
            <button className="admin-btn" onClick={() => openSheet(s)} data-testid={`hw-student-sheet-${i}`}>Sunday sheet</button>
            <button className="admin-btn" onClick={async () => { await adminApi.post(`${HW}/admin/students/${s.student_id}/grant-program`); adminApi.get(`${HW}/admin/students`).then(({ data }) => setStudents(data.students)); }} data-testid={`hw-student-grant-${i}`}>Grant program</button>
            <button className="admin-btn" onClick={() => openHistory(s.student_id)} data-testid={`hw-student-history-${i}`}>{openId === s.student_id ? "Close" : "History"}</button>
          </div>
          {openId === s.student_id && (
            history ? (
              <div style={{ marginTop: 10, background: "#0c1626", borderRadius: 10, padding: 12 }} data-testid="hw-student-history-panel">
                <p className="lead-sub">
                  Subscription: {history.entitlement ? `${history.entitlement.quota_used}/${history.entitlement.quota_total} used · till ${fmtTs(history.entitlement.valid_until)}` : "none"} ·
                  Questions evaluated: {history.analytics.total_awarded}/{history.analytics.total_max} marks ({history.analytics.percentage}%)
                </p>
                {history.analytics.error_frequency.length > 0 && (
                  <p className="lead-sub" style={{ color: "#ff9e9e" }} data-testid="hw-history-errors">
                    Common errors: {history.analytics.error_frequency.map((e) => `${e.category} (${e.count})`).join(", ")}
                  </p>)}
                {history.assignments.map((a, j) => (
                  <div className="admin-row" key={a.assignment_id} style={{ borderTop: "1px solid #22314a", padding: "6px 0" }} data-testid={`hw-history-row-${j}`}>
                    <span className={`status-pill ${a.status === "published" ? "approved" : a.status === "submitted" ? "review" : "locked"}`}>{a.status.toUpperCase()}</span>
                    <span style={{ flex: 1, color: "#dbe6f4", fontSize: 12 }}>{a.title} <small className="lead-sub">· {fmtTs(a.created_at)} · {a.question_count}q{a.submission ? ` · ${a.submission.file_count} files · v${(a.submission.versions || 0) + 1}` : ""}</small></span>
                    {a.evaluation?.status === "published" && <b style={{ color: "#4ade80" }}>{a.evaluation.total}/{a.evaluation.max_total}</b>}
                  </div>
                ))}
                {history.assignments.length === 0 && <p className="lead-sub">No assignments yet.</p>}
                <div style={{ marginTop: 10 }}>
                  <p className="lead-sub">Final note for this student's 30-day report:</p>
                  <textarea className="admin-input" rows={2} style={{ width: "100%" }} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="hw-report-comment" placeholder="e.g. Great improvement in Laws of Motion — focus on unit conversions next month…" />
                  <div className="admin-row" style={{ marginTop: 6 }}>
                    <button className="admin-btn" onClick={saveComment} data-testid="hw-report-comment-save">Save note</button>
                    {notice && <span className="lead-sub" style={{ color: "#4ade80" }}>{notice}</span>}
                  </div>
                </div>
              </div>
            ) : <p className="lead-sub" style={{ marginTop: 8 }}>Loading history…</p>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- Syllabus ---------- */
export function HwSyllabus() {
  const [category, setCategory] = useState("class_11");
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ chapter: "", topic: "", subtopics: "" });
  const [error, setError] = useState("");
  const load = useCallback(() => adminApi.get(`${HW}/admin/syllabus`, { params: { category } }).then(({ data }) => setDocs(data.syllabus)).catch(() => {}), [category]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setError("");
    try {
      await adminApi.post(`${HW}/admin/syllabus`, { category, ...form });
      setForm({ chapter: "", topic: "", subtopics: "" });
      load();
    } catch (err) { setError(errText(err)); }
  };
  const toggle = async (s) => { await adminApi.put(`${HW}/admin/syllabus/${s.syllabus_id}`, { enabled: !s.enabled }); load(); };
  const rename = async (s) => {
    const topic = window.prompt("Rename topic:", s.topic);
    if (topic && topic !== s.topic) { await adminApi.put(`${HW}/admin/syllabus/${s.syllabus_id}`, { topic }); load(); }
  };
  const editSubtopics = async (s) => {
    const val = window.prompt("Subtopics (comma-separated):", (s.subtopics || []).join(", "));
    if (val !== null) { await adminApi.put(`${HW}/admin/syllabus/${s.syllabus_id}`, { subtopics: val }); load(); }
  };
  const remove = async (s) => {
    try { await adminApi.delete(`${HW}/admin/syllabus/${s.syllabus_id}`); load(); }
    catch (err) { setError(errText(err)); }
  };

  return (
    <div data-testid="hw-admin-syllabus">
      <div className="admin-row" style={{ flexWrap: "wrap" }}>
        <select className="admin-input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="hw-syl-category">
          {CATS.filter(([k]) => k).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input className="admin-input" placeholder="Chapter" value={form.chapter} onChange={(e) => setForm((c) => ({ ...c, chapter: e.target.value }))} data-testid="hw-syl-chapter" />
        <input className="admin-input" placeholder="New topic" value={form.topic} onChange={(e) => setForm((c) => ({ ...c, topic: e.target.value }))} data-testid="hw-syl-topic" />
        <input className="admin-input" placeholder="Subtopics (comma-separated, optional)" value={form.subtopics} onChange={(e) => setForm((c) => ({ ...c, subtopics: e.target.value }))} data-testid="hw-syl-subtopics" />
        <button className="admin-btn" disabled={!form.chapter || !form.topic} onClick={add} data-testid="hw-syl-add">Add topic</button>
      </div>
      {error && <p className="admin-error">{error}</p>}
      <div className="admin-card" style={{ marginTop: 12 }}>
        {docs.map((s, i) => (
          <div className="admin-row" key={s.syllabus_id} style={{ borderTop: i ? "1px solid #22314a" : "none", padding: "7px 0" }} data-testid={`hw-syl-row-${i}`}>
            <span className={`status-pill ${s.enabled ? "approved" : "locked"}`}>{s.enabled ? "ON" : "OFF"}</span>
            <b className="lead-sub" style={{ minWidth: 180 }}>{s.chapter}</b>
            <span style={{ flex: 1, color: "#eef4fc" }}>{s.topic}
              {(s.subtopics || []).length > 0 && <small className="lead-sub" style={{ display: "block" }} data-testid={`hw-syl-subs-${i}`}>{s.subtopics.join(" · ")}</small>}
            </span>
            <span className="lead-sub">{s.qcount} q</span>
            <button className="admin-btn" onClick={() => toggle(s)} data-testid={`hw-syl-toggle-${i}`}>{s.enabled ? "Disable" : "Enable"}</button>
            <button className="admin-btn" onClick={() => rename(s)} data-testid={`hw-syl-rename-${i}`}>Rename</button>
            <button className="admin-btn" onClick={() => editSubtopics(s)} data-testid={`hw-syl-subedit-${i}`}>Subtopics</button>
            <button className="admin-btn" onClick={() => remove(s)} data-testid={`hw-syl-del-${i}`}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Question bank ---------- */
export function HwQuestions() {
  const [filters, setFilters] = useState({ category: "", status: "", search: "" });
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    adminApi.get(`${HW}/admin/questions`, { params: { ...filters, limit: 300 } })
      .then(({ data }) => setQuestions(data.questions)).catch((e) => setError(errText(e)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const review = async (qid, action) => { await adminApi.post(`${HW}/admin/questions/${qid}/review`, { action }); load(); };
  const remove = async (qid) => {
    try { await adminApi.delete(`${HW}/admin/questions/${qid}`); load(); }
    catch (err) { setError(errText(err)); }
  };
  const approveAll = async () => {
    const { data } = await adminApi.post(`${HW}/admin/questions/bulk-review`, { action: "approve" });
    load();
    alert(`Approved ${data.count} questions.`);
  };

  return (
    <div data-testid="hw-admin-questions">
      <div className="admin-row" style={{ flexWrap: "wrap" }}>
        <select className="admin-input" value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} data-testid="hw-q-filter-category">{CATS.map(([k, l]) => <option key={k} value={k}>{l || "All classes"}</option>)}</select>
        <select className="admin-input" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="hw-q-filter-status">
          <option value="">Any status</option><option value="pending">Pending review</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
        </select>
        <input className="admin-input" placeholder="Search text…" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="hw-q-search" />
        <button className="admin-btn" onClick={approveAll} data-testid="hw-q-approve-all">Approve all pending</button>
        <button className="admin-btn" onClick={load}><RefreshCw size={13} /></button>
      </div>
      {error && <p className="admin-error">{error}</p>}
      <p className="lead-sub" style={{ marginTop: 8 }}>{questions.length} questions</p>
      {questions.map((q, i) => (
        <div className="admin-card" key={q.question_id} data-testid={`hw-q-row-${i}`}>
          <div className="admin-row" style={{ flexWrap: "wrap" }}>
            <span className={`status-pill ${q.approval_status === "approved" ? "approved" : q.approval_status === "rejected" ? "failed" : "review"}`}>{q.approval_status.toUpperCase()}</span>
            <span className="status-pill locked">{q.source_type?.toUpperCase()}</span>
            <span className="lead-sub">{q.category} · {q.chapter} · {q.topic} · {q.difficulty} · {q.qtype} · {q.max_marks}m</span>
          </div>
          <p style={{ color: "#dbe6f4", fontSize: 13, margin: "8px 0" }}>{q.text.slice(0, 220)}{q.text.length > 220 ? "…" : ""}</p>
          {q.flag_reason && <p className="lead-sub" style={{ color: "#ff9e9e" }}>{q.flag_reason}</p>}
          {q.answer && <p className="lead-sub" style={{ color: "#4ade80" }}>Answer: {q.answer}</p>}
          <div className="admin-row">
            {q.approval_status !== "approved" && <button className="admin-btn" onClick={() => review(q.question_id, "approve")} data-testid={`hw-q-approve-${i}`}><CheckCircle2 size={13} /> Approve</button>}
            {q.approval_status !== "rejected" && <button className="admin-btn" onClick={() => review(q.question_id, "reject")} data-testid={`hw-q-reject-${i}`}>Reject</button>}
            <button className="admin-btn" onClick={() => remove(q.question_id)} data-testid={`hw-q-del-${i}`}><Trash2 size={13} /></button>
          </div>
        </div>
      ))}
      {questions.length === 0 && <div className="admin-empty">No questions match. Upload PDFs or generate with AI.</div>}
    </div>
  );
}

/* ---------- Bulk upload ---------- */
export function HwUpload() {
  const [sel, setSel] = useState({ category: "", chapter: "", topic: "" });
  const [meta, setMeta] = useState({ difficulty: "Medium", qtype: "short_answer", source_name: "", max_marks: 5, subtopic: "" });
  const [files, setFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => adminApi.get(`${HW}/admin/uploads`).then(({ data }) => setJobs(data.jobs)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!jobs.some((j) => j.status === "processing")) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const upload = async () => {
    if (!files.length || !sel.category || !sel.chapter || !sel.topic) { setError("Pick class/chapter/topic and at least one PDF."); return; }
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      Object.entries(sel).forEach(([k, v]) => form.append(k, v));
      Object.entries(meta).forEach(([k, v]) => form.append(k, v));
      const { data } = await adminApi.post(`${HW}/admin/uploads`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setNotice(`${data.jobs.length} PDF(s) queued — extraction runs in the background.${data.rejected.length ? ` Rejected: ${data.rejected.join("; ")}` : ""}`);
      setFiles([]);
      load();
    } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  const retry = async (jobId) => { await adminApi.post(`${HW}/admin/uploads/${jobId}/retry`); load(); };

  return (
    <div data-testid="hw-admin-upload">
      <div className="admin-card">
        <div className="admin-row" style={{ flexWrap: "wrap" }}>
          <CatChapterTopic value={sel} onChange={setSel} prefix="hw-up" />
          <select className="admin-input" value={meta.difficulty} onChange={(e) => setMeta((m) => ({ ...m, difficulty: e.target.value }))} data-testid="hw-up-difficulty">
            {["Easy", "Medium", "Hard", "JEE Main", "JEE Advanced", "NEET Level"].map((d) => <option key={d}>{d}</option>)}
          </select>
          <select className="admin-input" value={meta.qtype} onChange={(e) => setMeta((m) => ({ ...m, qtype: e.target.value }))} data-testid="hw-up-qtype">
            {QTYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <input className="admin-input" style={{ maxWidth: 160 }} placeholder="Source (e.g. NCERT Exemplar)" value={meta.source_name} onChange={(e) => setMeta((m) => ({ ...m, source_name: e.target.value }))} data-testid="hw-up-source" />
          <input className="admin-input" style={{ maxWidth: 180 }} placeholder="Subtopic (optional)" value={meta.subtopic} onChange={(e) => setMeta((m) => ({ ...m, subtopic: e.target.value }))} data-testid="hw-up-subtopic" />
        </div>
        <div className="admin-row" style={{ marginTop: 10, flexWrap: "wrap" }}>
          <input type="file" accept="application/pdf" multiple onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 10))} data-testid="hw-up-files" style={{ color: "#9db1c9", fontSize: 13 }} />
          <button className="admin-btn" disabled={busy || !files.length} onClick={upload} data-testid="hw-up-submit">
            <UploadCloud size={14} /> {busy ? "Uploading…" : `Upload ${files.length || ""} PDF${files.length === 1 ? "" : "s"} & extract`}
          </button>
        </div>
        {error && <p className="admin-error">{error}</p>}
        {notice && <p className="lead-sub" style={{ color: "#4ade80" }} data-testid="hw-up-notice">{notice}</p>}
      </div>
      {jobs.map((j, i) => (
        <div className="admin-card" key={j.job_id} data-testid={`hw-job-${i}`}>
          <div className="admin-row">
            <span className={`status-pill ${j.status === "ready" ? "approved" : j.status === "failed" ? "failed" : "review"}`} data-testid={`hw-job-status-${i}`}>
              {j.status === "processing" ? (j.progress || "PROCESSING…") : j.status.toUpperCase()}</span>
            <b style={{ color: "#eef4fc", flex: 1 }}>{j.filename}</b>
            <span className="lead-sub">{j.category} · {j.chapter} · {j.topic}</span>
          </div>
          {j.status === "ready" && j.stats && (
            <p className="lead-sub" style={{ marginTop: 6 }} data-testid={`hw-job-stats-${i}`}>
              Detected {j.stats.detected} · Extracted {j.stats.extracted} · Awaiting review {j.stats.awaiting_review} · Duplicates {j.stats.duplicates} · Errors {j.stats.errors}
            </p>)}
          {j.status === "failed" && <p className="admin-error" style={{ marginTop: 6 }}>{j.error}</p>}
          <div className="admin-row" style={{ marginTop: 6 }}>
            {j.status === "failed" && <button className="admin-btn" onClick={() => retry(j.job_id)} data-testid={`hw-job-retry-${i}`}>Retry</button>}
            {j.status === "ready" && <span className="lead-sub">Review in Question Bank tab (filter: Pending review)</span>}
          </div>
        </div>
      ))}
      {jobs.length === 0 && <div className="admin-empty">No uploads yet.</div>}
    </div>
  );
}

/* ---------- AI generate ---------- */
export function HwAiGen() {
  const [sel, setSel] = useState({ category: "", chapter: "", topic: "" });
  const [opts, setOpts] = useState({ difficulty: "Medium", qtype: "mcq", count: 5 });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!sel.category || !sel.chapter || !sel.topic) { setError("Pick class/chapter/topic first."); return; }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await adminApi.post(`${HW}/admin/ai-generate`, { ...sel, ...opts });
      setResult(data);
    } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };

  return (
    <div className="admin-card" data-testid="hw-admin-aigen">
      <p className="lead-sub">Generate original questions with AI. They land in the Question Bank as <b>pending review</b> — approve them before students get them.</p>
      <div className="admin-row" style={{ flexWrap: "wrap" }}>
        <CatChapterTopic value={sel} onChange={setSel} prefix="hw-ai" />
        <select className="admin-input" value={opts.difficulty} onChange={(e) => setOpts((o) => ({ ...o, difficulty: e.target.value }))} data-testid="hw-ai-difficulty">
          {["Easy", "Medium", "Hard", "JEE Main", "JEE Advanced", "NEET Level"].map((d) => <option key={d}>{d}</option>)}
        </select>
        <select className="admin-input" value={opts.qtype} onChange={(e) => setOpts((o) => ({ ...o, qtype: e.target.value }))} data-testid="hw-ai-qtype">
          {QTYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
        <input className="admin-input" type="number" min="1" max="20" style={{ maxWidth: 90 }} value={opts.count} onChange={(e) => setOpts((o) => ({ ...o, count: e.target.value }))} data-testid="hw-ai-count" />
        <button className="admin-btn" disabled={busy} onClick={generate} data-testid="hw-ai-generate">
          <Sparkles size={14} /> {busy ? "Generating…" : "Generate"}
        </button>
      </div>
      {error && <p className="admin-error">{error}</p>}
      {result && (
        <div style={{ marginTop: 12 }} data-testid="hw-ai-result">
          <p className="lead-sub" style={{ color: "#4ade80" }}>{result.created} questions created — review & approve in the Question Bank tab.</p>
          {result.questions.map((q, i) => (
            <p key={q.question_id} className="lead-sub" style={{ borderTop: "1px solid #22314a", padding: "6px 0" }} data-testid={`hw-ai-q-${i}`}>
              <b>Q{i + 1}.</b> {q.text.slice(0, 140)}{q.text.length > 140 ? "…" : ""} {q.answer && <span style={{ color: "#4ade80" }}>· Ans: {q.answer}</span>}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Create compulsory homework ---------- */
function HwAssign() {
  const [sel, setSel] = useState({ category: "", chapter: "", topic: "" });
  const [opts, setOpts] = useState({ count: 10, deadline_days: 3, title: "", instructions: "", release_in_days: 0 });
  const [target, setTarget] = useState("category");
  const [targetStudent, setTargetStudent] = useState("");
  const [students, setStudents] = useState([]);
  const [list, setList] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const load = useCallback(() => adminApi.get(`${HW}/admin/assignments`).then(({ data }) => setList(data.assignments)).catch(() => {}), []);
  useEffect(() => { load(); adminApi.get(`${HW}/admin/students`).then(({ data }) => setStudents(data.students)).catch(() => {}); }, [load]);

  const create = async () => {
    setError("");
    try {
      const payload = { ...sel, ...opts, target };
      if (target === "student") {
        if (!targetStudent) { setError("Pick a student for individual targeting."); return; }
        payload.student_id = targetStudent;
      }
      if (target === "all") payload.category = "class_11"; // backend ignores category for target=all but validates it
      await adminApi.post(`${HW}/admin/assignments`, payload);
      setNotice("Assignment published — targeted students get it on their dashboard (and by email).");
      load();
    } catch (err) { setError(errText(err)); }
  };
  const unpublish = async (id) => { await adminApi.delete(`${HW}/admin/assignments/${id}`); load(); };

  return (
    <div data-testid="hw-admin-assign">
      <div className="admin-card">
        <p className="lead-sub">Admin-controlled delivery: students cannot generate their own homework — they receive exactly what you publish, when you publish it.</p>
        <div className="admin-row" style={{ flexWrap: "wrap" }}>
          <select className="admin-input" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="hw-as-target">
            <option value="category">Whole class / exam</option>
            <option value="student">Individual student</option>
            <option value="all">All students</option>
          </select>
          {target === "student" && (
            <select className="admin-input" value={targetStudent} onChange={(e) => setTargetStudent(e.target.value)} data-testid="hw-as-student">
              <option value="">Pick student…</option>
              {students.map((s) => <option key={s.student_id} value={s.student_id}>{s.name} ({s.category_label})</option>)}
            </select>
          )}
          <CatChapterTopic value={sel} onChange={setSel} prefix="hw-as" />
        </div>
        <div className="admin-row" style={{ flexWrap: "wrap", marginTop: 8 }}>
          <input className="admin-input" style={{ maxWidth: 200 }} placeholder="Title (e.g. Practice Set 001)" value={opts.title} onChange={(e) => setOpts((o) => ({ ...o, title: e.target.value }))} data-testid="hw-as-title" />
          <label className="lead-sub">Questions <input className="admin-input" type="number" min="1" max="50" style={{ maxWidth: 80 }} value={opts.count} onChange={(e) => setOpts((o) => ({ ...o, count: e.target.value }))} data-testid="hw-as-count" /></label>
          <label className="lead-sub">Release in days <input className="admin-input" type="number" min="0" max="90" style={{ maxWidth: 80 }} value={opts.release_in_days} onChange={(e) => setOpts((o) => ({ ...o, release_in_days: e.target.value }))} data-testid="hw-as-release" /></label>
          <label className="lead-sub">Deadline days <input className="admin-input" type="number" min="1" max="30" style={{ maxWidth: 80 }} value={opts.deadline_days} onChange={(e) => setOpts((o) => ({ ...o, deadline_days: e.target.value }))} data-testid="hw-as-deadline" /></label>
        </div>
        <textarea className="admin-input" rows={2} style={{ width: "100%", marginTop: 8 }} placeholder="Instructions for students (optional)…" value={opts.instructions} onChange={(e) => setOpts((o) => ({ ...o, instructions: e.target.value }))} data-testid="hw-as-instructions" />
        <div className="admin-row" style={{ marginTop: 8 }}>
          <button className="admin-btn" disabled={!sel.topic} onClick={create} data-testid="hw-as-create">Publish assignment</button>
        </div>
        {error && <p className="admin-error">{error}</p>}
        {notice && <p className="lead-sub" style={{ color: "#4ade80" }} data-testid="hw-as-notice">{notice}</p>}
      </div>
      {list.map((a, i) => (
        <div className="admin-card" key={a.assignment_id} data-testid={`hw-as-row-${i}`}>
          <div className="admin-row">
            <span className="status-pill review">{a.target === "student" ? "INDIVIDUAL" : a.target === "all" ? "ALL" : "COMPULSORY"}</span>
            <b style={{ color: "#eef4fc", flex: 1 }}>{a.title || `${a.chapter} — ${a.topic}`}</b>
            <span className="lead-sub">{a.category} · {a.question_ids.length} q · due {fmtTs(a.deadline)}</span>
            <span className="lead-sub">{a.submitted}/{a.instances} submitted</span>
            <button className="admin-btn" onClick={() => unpublish(a.assignment_id)} data-testid={`hw-as-unpub-${i}`}>Unpublish</button>
          </div>
        </div>
      ))}
      {list.length === 0 && <div className="admin-empty">No assignments published yet.</div>}
    </div>
  );
}

/* ---------- Submissions + evaluation ---------- */
const ERROR_CATS = ["Conceptual Error", "Formula Error", "Calculation Error", "Unit Error", "Sign Error",
                    "Diagram Error", "Missing Steps", "Incomplete Solution", "Incorrect Substitution",
                    "Misread Question", "Other"];
const QSTATUS = [["", "—"], ["correct", "Correct"], ["partially_correct", "Partially correct"],
                 ["incorrect", "Incorrect"], ["not_attempted", "Not attempted"], ["needs_clarification", "Needs clarification"]];

function HwSubmissions() {
  const [filters, setFilters] = useState({ category: "", status: "", search: "" });
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [marks, setMarks] = useState({});
  const [feedback, setFeedback] = useState("");
  const [releaseSol, setReleaseSol] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [viewFile, setViewFile] = useState(0);
  const [zoom, setZoom] = useState(100);

  const load = useCallback(() => {
    adminApi.get(`${HW}/admin/submissions`, { params: filters }).then(({ data }) => setRows(data.submissions)).catch((e) => setError(errText(e)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (sid) => {
    if (open === sid) { setOpen(null); return; }
    setOpen(sid);
    setDetail(null);
    setError("");
    setViewFile(0);
    setZoom(100);
    const { data } = await adminApi.get(`${HW}/admin/submissions/${sid}`);
    setDetail(data);
    const init = {};
    (data.submission.evaluation?.per_question || []).forEach((m) => {
      init[m.question_id] = { awarded: m.awarded, comment: m.comment, qstatus: m.qstatus || "", error_category: m.error_category || "" };
    });
    setMarks(init);
    setFeedback(data.submission.evaluation?.feedback || "");
    setReleaseSol(data.submission.evaluation?.release_solutions || false);
  };

  const evaluate = async (action) => {
    setError("");
    try {
      const payload = { action, feedback, release_solutions: releaseSol,
        marks: Object.entries(marks).map(([question_id, m]) => ({ question_id, awarded: Number(m.awarded) || 0,
          comment: m.comment || "", qstatus: m.qstatus || "", error_category: m.error_category || "" })) };
      if (action === "reject") payload.reject_reason = feedback;
      const { data } = await adminApi.post(`${HW}/admin/submissions/${open}/evaluate`, payload);
      setNotice(`${action === "publish" ? "Published" : action === "save_draft" ? "Draft saved" : action === "request_corrections" ? "Corrections requested" : "Rejected"} — total ${data.total}/${data.max_total} (${data.percentage}%)`);
      load();
      setOpen(null);
    } catch (err) { setError(errText(err)); }
  };

  const total = detail ? detail.questions.reduce((s, q) => s + (Number(marks[q.question_id]?.awarded) || 0), 0) : 0;
  const maxTotal = detail ? detail.questions.reduce((s, q) => s + q.max_marks, 0) : 0;
  const curFile = detail?.submission?.files?.[viewFile];

  return (
    <div data-testid="hw-admin-submissions">
      <div className="admin-row" style={{ flexWrap: "wrap" }}>
        <select className="admin-input" value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} data-testid="hw-sub-category">{CATS.map(([k, l]) => <option key={k} value={k}>{l || "All classes"}</option>)}</select>
        <select className="admin-input" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="hw-sub-status">
          <option value="">Any status</option><option value="pending">Pending evaluation</option><option value="published">Evaluated</option>
          <option value="correction_requested">Corrections required</option><option value="rejected">Rejected</option>
        </select>
        <input className="admin-input" placeholder="Search student/chapter…" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="hw-sub-search" />
        <button className="admin-btn" onClick={load}><RefreshCw size={13} /></button>
      </div>
      {error && <p className="admin-error">{error}</p>}
      {notice && <p className="lead-sub" style={{ color: "#4ade80" }} data-testid="hw-eval-notice">{notice}</p>}
      {rows === null && <div className="admin-empty">Loading…</div>}
      {rows?.length === 0 && <div className="admin-empty" data-testid="hw-sub-empty">No submissions yet.</div>}
      {rows?.map((r, i) => (
        <div className="admin-card" key={r.submission_id} data-testid={`hw-sub-row-${i}`}>
          <div className="admin-row" style={{ flexWrap: "wrap" }}>
            <span className={`status-pill ${r.status === "pending" ? "review" : r.status === "published" ? "approved" : "failed"}`} data-testid={`hw-sub-status-${i}`}>{r.status.replace(/_/g, " ").toUpperCase()}</span>
            <b style={{ color: "#eef4fc" }}>{r.student_name}</b>
            <span className="lead-sub">{r.category_label} · {r.chapter} · {r.topic}</span>
            <span className="lead-sub">{r.assignment_id} · {fmtTs(r.submitted_at)} · {r.file_count} files</span>
            {r.total != null && <b style={{ color: "#4ade80" }}>{r.total}/{r.max_total}</b>}
            <button className="admin-btn" onClick={() => openDetail(r.submission_id)} data-testid={`hw-sub-open-${i}`}><FileUp size={13} /> {open === r.submission_id ? "Close" : "Evaluate"}</button>
          </div>
          {open === r.submission_id && (
            detail ? (
              <div style={{ marginTop: 12 }} data-testid="hw-eval-panel">
                <p className="lead-sub">Manual evaluation — marks and labels are entered by you only; the system never auto-grades.</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="hw-eval-split">
                  {/* left: student's uploaded work */}
                  <div style={{ background: "#0c1626", borderRadius: 10, padding: 10 }} data-testid="hw-eval-viewer">
                    <div className="admin-row" style={{ marginBottom: 8 }}>
                      {detail.submission.files.map((f) => (
                        <button key={f.index} className="admin-btn" style={f.index === viewFile ? { background: "#2d70e4" } : {}}
                                onClick={() => { setViewFile(f.index); setZoom(100); }} data-testid={`hw-eval-filetab-${f.index}`}>{f.name.slice(0, 18)}</button>
                      ))}
                      <button className="admin-btn" onClick={() => setZoom((z) => Math.max(50, z - 25))} data-testid="hw-zoom-out">−</button>
                      <span className="lead-sub">{zoom}%</span>
                      <button className="admin-btn" onClick={() => setZoom((z) => Math.min(300, z + 25))} data-testid="hw-zoom-in">+</button>
                      {curFile && <a className="admin-btn" style={{ textDecoration: "none" }} target="_blank" rel="noreferrer"
                                     href={`${process.env.REACT_APP_BACKEND_URL}/api/hw/files/${detail.submission.submission_id}/${curFile.index}`} data-testid="hw-eval-download">⤓</a>}
                    </div>
                    {curFile && (curFile.name.toLowerCase().endsWith(".pdf") ? (
                      <iframe title="submission" src={`${process.env.REACT_APP_BACKEND_URL}/api/hw/files/${detail.submission.submission_id}/${curFile.index}`}
                              style={{ width: "100%", height: 480, border: "none", background: "#fff", borderRadius: 6 }} data-testid="hw-eval-frame" />
                    ) : (
                      <div style={{ overflow: "auto", maxHeight: 480, textAlign: "center" }}>
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/hw/files/${detail.submission.submission_id}/${curFile.index}`}
                             alt={curFile.name} style={{ width: `${zoom}%`, borderRadius: 6 }} data-testid="hw-eval-img" />
                      </div>
                    ))}
                  </div>
                  {/* right: per-question evaluation */}
                  <div data-testid="hw-eval-questions">
                    {detail.questions.map((q) => {
                      const typed = detail.submission.text_answers?.[q.question_id];
                      const mcqChosen = detail.submission.mcq_answers?.[q.question_id];
                      const sugg = detail.submission.ai_suggestions?.items?.find((x) => x.question_id === q.question_id);
                      return (
                      <div key={q.question_id} style={{ borderTop: "1px solid #22314a", padding: "8px 0" }} data-testid={`hw-eval-q-${q.qno}`}>
                        <p style={{ color: "#dbe6f4", fontSize: 12, margin: "0 0 6px" }}><b>Q{q.qno}.</b> {q.text.slice(0, 120)}{q.text.length > 120 ? "…" : ""} <span className="lead-sub">({q.max_marks}m)</span></p>
                        {q.answer && <p className="lead-sub" style={{ color: "#4ade80", margin: "0 0 6px" }}>Expected: {q.answer}</p>}
                        {mcqChosen && <p className="lead-sub" style={{ color: "#7dd3fc", margin: "0 0 6px" }} data-testid={`hw-eval-mcq-${q.qno}`}>Student selected: {mcqChosen}</p>}
                        {typed && <p className="lead-sub" style={{ color: "#e2e8f0", margin: "0 0 6px", background: "#111d30", padding: 8, borderRadius: 6 }} data-testid={`hw-eval-typed-${q.qno}`}>Student typed: {typed}</p>}
                        {sugg && (
                          <div style={{ background: "#1a2740", border: "1px dashed #7dd3fc", borderRadius: 8, padding: 8, margin: "0 0 6px" }} data-testid={`hw-eval-ai-${q.qno}`}>
                            <span className="lead-sub" style={{ color: "#7dd3fc" }}>
                              AI draft suggestion: {sugg.suggested_marks}/{q.max_marks}{sugg.needs_review ? " · NEEDS YOUR REVIEW" : ""} — {sugg.rubric_notes}</span>
                            <button className="admin-btn" style={{ marginLeft: 8, padding: "2px 10px", fontSize: 10 }}
                                    onClick={() => setMarks((m) => ({ ...m, [q.question_id]: { ...m[q.question_id], awarded: sugg.suggested_marks, comment: sugg.rubric_notes } }))}
                                    data-testid={`hw-eval-apply-${q.qno}`}>Apply</button>
                          </div>
                        )}
                        <div className="admin-row" style={{ flexWrap: "wrap", gap: 6 }}>
                          <input className="admin-input" type="number" min="0" max={q.max_marks} step="0.5" style={{ maxWidth: 76 }}
                                 placeholder={`0-${q.max_marks}`} value={marks[q.question_id]?.awarded ?? ""}
                                 onChange={(e) => setMarks((m) => ({ ...m, [q.question_id]: { ...m[q.question_id], awarded: e.target.value } }))}
                                 data-testid={`hw-eval-marks-${q.qno}`} />
                          <select className="admin-input" style={{ maxWidth: 150 }} value={marks[q.question_id]?.qstatus || ""}
                                  onChange={(e) => setMarks((m) => ({ ...m, [q.question_id]: { ...m[q.question_id], qstatus: e.target.value } }))}
                                  data-testid={`hw-eval-status-${q.qno}`}>
                            {QSTATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                          </select>
                          <select className="admin-input" style={{ maxWidth: 160 }} value={marks[q.question_id]?.error_category || ""}
                                  onChange={(e) => setMarks((m) => ({ ...m, [q.question_id]: { ...m[q.question_id], error_category: e.target.value } }))}
                                  data-testid={`hw-eval-error-${q.qno}`}>
                            <option value="">Error category…</option>
                            {ERROR_CATS.map((c) => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <input className="admin-input" style={{ width: "100%", marginTop: 6 }} placeholder="Remark for this question (optional)"
                               value={marks[q.question_id]?.comment || ""}
                               onChange={(e) => setMarks((m) => ({ ...m, [q.question_id]: { ...m[q.question_id], comment: e.target.value } }))}
                               data-testid={`hw-eval-comment-${q.qno}`} />
                      </div>
                      );
                    })}
                  </div>
                </div>
                <div className="admin-row" style={{ marginTop: 10 }} data-testid="hw-eval-total">
                  <b style={{ color: "#fbbf24", fontSize: 17 }}>Total: {total}/{maxTotal} ({maxTotal ? Math.round(total / maxTotal * 100) : 0}%)</b>
                  <label className="lead-sub" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" style={{ width: 16, height: 16, flex: "none", margin: 0, padding: 0 }} checked={releaseSol} onChange={(e) => setReleaseSol(e.target.checked)} data-testid="hw-eval-release" /> Release solutions with result
                  </label>
                </div>
                <textarea className="admin-input" rows={2} placeholder="Overall feedback for the student…" value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid="hw-eval-feedback" style={{ width: "100%", marginTop: 8 }} />
                {detail.submission.evaluation?.evaluated_by && (
                  <p className="lead-sub" style={{ marginTop: 6 }} data-testid="hw-eval-by">Last evaluated by {detail.submission.evaluation.evaluated_by} · {fmtTs(detail.submission.evaluation.evaluated_at)} · every change is audit-logged</p>
                )}
                <div className="admin-row" style={{ marginTop: 10, flexWrap: "wrap" }}>
                  <button className="admin-btn" onClick={() => evaluate("save_draft")} data-testid="hw-eval-draft">Save draft</button>
                  <button className="admin-btn" style={{ background: "#12b76a" }} onClick={() => evaluate("publish")} data-testid="hw-eval-publish">Approve & publish result</button>
                  <button className="admin-btn" style={{ background: "#b45309" }} onClick={() => evaluate("request_corrections")} data-testid="hw-eval-corrections">Request corrections</button>
                  <button className="admin-btn" style={{ background: "#b42318" }} onClick={() => evaluate("reject")} data-testid="hw-eval-reject">Reject</button>
                </div>
              </div>
            ) : <p className="lead-sub" style={{ marginTop: 8 }}>Loading submission…</p>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- Payments ---------- */
function HwPayments() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { adminApi.get(`${HW}/admin/payments`).then(({ data }) => setOrders(data.orders)).catch(() => setOrders([])); }, []);
  if (!orders) return <div className="admin-empty">Loading…</div>;
  return (
    <div className="admin-card" data-testid="hw-admin-payments">
      {orders.length === 0 && <div className="admin-empty">No homework program orders yet.</div>}
      {orders.map((o, i) => (
        <div className="admin-row" key={o.order_id} style={{ borderTop: i ? "1px solid #22314a" : "none", padding: "8px 0" }} data-testid={`hw-order-${i}`}>
          <span className={`status-pill ${o.status === "paid" ? "approved" : o.status === "failed" ? "failed" : "review"}`}>{o.status.toUpperCase()}</span>
          <span style={{ flex: 1, color: "#eef4fc" }}>{o.student_name} <small className="lead-sub">{o.order_id}</small></span>
          <b>{inr(o.amount_paise)}</b>
          <span className="lead-sub">{fmtTs(o.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------- AI Bank Fill ---------- */
const FILL_CATS = [["class_11", "Class 11"], ["class_12", "Class 12"], ["class_10", "Class 10"],
                   ["class_9", "Class 9"], ["neet", "NEET UG"], ["jee_main", "JEE Main"], ["jee_advanced", "JEE Advanced"],
                   ["class_6", "Class 6"], ["class_7", "Class 7"], ["class_8", "Class 8"]];

export function HwBankFill() {
  const [cats, setCats] = useState(FILL_CATS.map(([k]) => k));
  const [perSubtopic, setPerSubtopic] = useState(50);
  const [spt, setSpt] = useState(4);
  const [job, setJob] = useState(null);
  const [msg, setMsg] = useState("");
  const [coverage, setCoverage] = useState(null);
  const [showGaps, setShowGaps] = useState("");

  const load = useCallback(() => adminApi.get(`${HW}/admin/bank-fill/status`).then(({ data }) => setJob(data.job)).catch(() => {}), []);
  const loadCoverage = useCallback(() => adminApi.get(`${HW}/admin/coverage`).then(({ data }) => setCoverage(data.coverage)).catch(() => {}), []);
  useEffect(() => { load(); loadCoverage(); }, [load, loadCoverage]);
  useEffect(() => {
    if (job?.status !== "running") return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [job?.status, load]);

  const act = async (path, body = {}) => {
    setMsg("");
    try {
      await adminApi.post(`${HW}/admin/bank-fill/${path}`, body);
      load();
    } catch (e) { setMsg(e.response?.data?.detail || "Action failed"); }
  };
  const toggleCat = (k) => setCats((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  const pct = job && job.total_items ? Math.round((job.done_items + job.failed_items) / job.total_items * 100) : 0;

  return (
    <div className="admin-card" data-testid="hw-bank-fill">
      <h4>AI Bank Fill — 50 questions per subtopic</h4>
      <p className="lead-sub" style={{ fontSize: 11 }}>
        AI first lists subtopics for every topic, then generates an Easy/Medium/Hard spread (40/40/20) of MCQ questions per subtopic (auto-approved).
        Runs in the background for hours, resumes automatically after restarts. AI writes questions only — it never grades.</p>
      <div className="admin-row" style={{ flexWrap: "wrap", gap: 10, marginTop: 10 }}>
        {FILL_CATS.map(([k, l]) => (
          <label key={k} className="lead-sub" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#c7d5e8" }}>
            <input type="checkbox" style={{ width: 15, height: 15, flex: "none", margin: 0, padding: 0 }}
                   checked={cats.includes(k)} onChange={() => toggleCat(k)} data-testid={`hw-fill-cat-${k}`} /> {l}
          </label>
        ))}
      </div>
      <div className="admin-row" style={{ flexWrap: "wrap", gap: 14, marginTop: 10 }}>
        <label className="lead-sub" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#c7d5e8" }}>
          Questions per subtopic
          <input className="admin-input" type="number" style={{ maxWidth: 90 }} value={perSubtopic}
                 onChange={(e) => setPerSubtopic(Number(e.target.value))} data-testid="hw-fill-per-subtopic" />
        </label>
        <label className="lead-sub" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#c7d5e8" }}>
          Subtopics per topic (AI)
          <input className="admin-input" type="number" style={{ maxWidth: 90 }} value={spt}
                 onChange={(e) => setSpt(Number(e.target.value))} data-testid="hw-fill-spt" />
        </label>
      </div>
      <div className="admin-row" style={{ marginTop: 12, gap: 10 }}>
        <button className="admin-btn" disabled={!cats.length || job?.status === "running"}
                onClick={() => act("start", { categories: cats, per_subtopic: perSubtopic, subtopics_per_topic: spt })}
                data-testid="hw-fill-start">Start fill</button>
        {job?.status === "running" && <button className="admin-btn" onClick={() => { if (window.confirm("Stop the AI bank fill? Progress is saved and you can resume anytime.")) act("stop"); }} data-testid="hw-fill-stop">Stop</button>}
        {job?.status === "paused" && <button className="admin-btn" onClick={() => act("resume")} data-testid="hw-fill-resume">Resume</button>}
        {msg && <span className="admin-error" data-testid="hw-fill-msg">{msg}</span>}
      </div>
      {job && (
        <div style={{ marginTop: 16, borderTop: "1px solid #22314a", paddingTop: 12 }} data-testid="hw-fill-status">
          <div className="admin-row" style={{ justifyContent: "space-between" }}>
            <b style={{ color: job.status === "running" ? "#4ade80" : job.status === "done" ? "#7dd3fc" : "#fbbf24" }}
               data-testid="hw-fill-state">{job.status.toUpperCase()}</b>
            <span className="lead-sub">{job.done_items + job.failed_items}/{job.total_items || "…"} subtopics · {job.stats.generated} questions · {job.stats.calls} AI calls</span>
          </div>
          <div style={{ height: 8, background: "#0c1727", borderRadius: 6, marginTop: 8, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "#1d4ed8", transition: "width .5s ease" }} data-testid="hw-fill-bar" />
          </div>
          {job.current_item && <p className="lead-sub" style={{ marginTop: 8 }} data-testid="hw-fill-current">Now filling: {job.current_item}</p>}
          {job.last_error && <p className="admin-error" data-testid="hw-fill-error">{job.last_error}</p>}
        </div>
      )}
      {coverage && (
        <div style={{ marginTop: 16, borderTop: "1px solid #22314a", paddingTop: 12 }} data-testid="hw-coverage">
          <h4 style={{ marginTop: 0 }}>Question-bank coverage (real database counts)</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 10 }}>
            {coverage.map((c) => (
              <div key={c.category} style={{ background: "#0c1727", borderRadius: 10, padding: 12 }} data-testid={`hw-cov-${c.category}`}>
                <div className="admin-row" style={{ justifyContent: "space-between" }}>
                  <b style={{ color: "#eef4fc", fontSize: 13 }}>{c.category_label}</b>
                  <span className="lead-sub">{c.questions} q</span>
                </div>
                <p className="lead-sub" style={{ margin: "6px 0 0", color: c.empty_topics.length ? "#ff9e9e" : "#4ade80" }} data-testid={`hw-cov-empty-${c.category}`}>
                  {c.empty_topics.length ? `${c.empty_topics.length}/${c.topics} topics empty` : "All topics covered"}</p>
                {c.empty_subtopics.length > 0 && <p className="lead-sub" style={{ margin: "2px 0 0", color: "#fbbf24" }}>{c.empty_subtopics.length} subtopics empty</p>}
                {(c.empty_topics.length > 0 || c.empty_subtopics.length > 0) && (
                  <button className="admin-btn" style={{ marginTop: 6, padding: "3px 10px", fontSize: 10 }}
                          onClick={() => setShowGaps(showGaps === c.category ? "" : c.category)}
                          data-testid={`hw-cov-show-${c.category}`}>
                    {showGaps === c.category ? "Hide gaps" : "Show gaps"}
                  </button>
                )}
                {showGaps === c.category && (
                  <div style={{ marginTop: 6 }} data-testid={`hw-cov-list-${c.category}`}>
                    {[...c.empty_topics, ...c.empty_subtopics].slice(0, 40).map((t) => (
                      <p key={t} className="lead-sub" style={{ margin: "2px 0", color: "#9db1c9" }}>{t}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <button className="admin-btn" style={{ marginTop: 10 }} onClick={loadCoverage} data-testid="hw-cov-refresh">Refresh coverage</button>
        </div>
      )}
    </div>
  );
}

/* ---------- Settings ---------- */
function HwSettings() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { adminApi.get(`${HW}/admin/settings`).then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="admin-empty">Loading…</div>;
  const save = async () => {
    await adminApi.put(`${HW}/admin/settings`, s);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };
  const Num = ({ k, label }) => (
    <label className="lead-sub" style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 10, fontSize: 12, color: "#c7d5e8", maxWidth: 640 }}>
      {label}
      <input className="admin-input" type="number" style={{ maxWidth: 100 }} value={s[k]} onChange={(e) => setS((c) => ({ ...c, [k]: Number(e.target.value) }))} data-testid={`hw-set-${k}`} />
    </label>
  );
  return (
    <div className="admin-card" data-testid="hw-admin-settings">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Num k="questions_per_assignment" label="Questions per homework (default 10)" />
        <Num k="default_max_marks" label="Default max marks per question" />
        <Num k="repeat_cooldown_days" label="Don't repeat a student's questions within (days)" />
        <Num k="daily_limit" label="Practice homework per student per day (0 = unlimited)" />
        <Num k="deadline_days" label="Homework deadline (days)" />
        <Num k="program_access_days" label="Subscription access duration (days) — default 365" />
        <Num k="program_quota" label="Subscription homework quota (assignments)" />
        <Num k="grace_days" label="Grace period after expiry (days, submissions still accepted)" />
        <Num k="report_days" label="Progress report period (days) — default 30" />
        <Num k="ai_cost_per_call_paise" label="Estimated AI cost per call (paise) — planning only" />
        <label className="lead-sub" style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 10, fontSize: 12, color: "#c7d5e8", maxWidth: 640 }}>
          <input type="checkbox" style={{ width: 16, height: 16, flex: "none", margin: 0, padding: 0 }} checked={s.require_subscription} onChange={(e) => setS((c) => ({ ...c, require_subscription: e.target.checked }))} data-testid="hw-set-require-subscription" />
          Require paid subscription (UNCHECK while testing so you can submit & review homework without payment)
        </label>
        <label className="lead-sub" style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 10, fontSize: 12, color: "#c7d5e8", maxWidth: 640 }}>
          <input type="checkbox" style={{ width: 16, height: 16, flex: "none", margin: 0, padding: 0 }} checked={s.supervised_practice} onChange={(e) => setS((c) => ({ ...c, supervised_practice: e.target.checked }))} data-testid="hw-set-supervised" />
          Allow supervised practice mode (students may self-generate practice homework — off = fully admin-assigned)
        </label>
        <label className="lead-sub" style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 10, fontSize: 12, color: "#c7d5e8", maxWidth: 640 }}>
          <input type="checkbox" style={{ width: 16, height: 16, flex: "none", margin: 0, padding: 0 }} checked={s.daily_enabled} onChange={(e) => setS((c) => ({ ...c, daily_enabled: e.target.checked }))} data-testid="hw-set-daily-enabled" />
          Practice homework generation enabled (when supervised mode is on)
        </label>
        <label className="lead-sub" style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 10, fontSize: 12, color: "#c7d5e8", maxWidth: 640 }}>
          <input type="checkbox" style={{ width: 16, height: 16, flex: "none", margin: 0, padding: 0 }} checked={s.notifications_enabled} onChange={(e) => setS((c) => ({ ...c, notifications_enabled: e.target.checked }))} data-testid="hw-set-notifications" />
          Email notifications (assigned / submitted / checked / corrections / expiring)
        </label>
        <div>
          <button className="admin-btn" onClick={save} data-testid="hw-set-save">Save settings</button>
          {saved && <span className="lead-sub" style={{ color: "#4ade80", marginLeft: 10 }} data-testid="hw-set-saved">Saved</span>}
        </div>
      </div>
    </div>
  );
}

/* ---------- main ---------- */
/* ---------- Review Desk (document-based homework v2) ---------- */
const HWD = "/hwd";
const HWD_STATUS = { submitted: ["WITH SIR", "review"], reviewing: ["REVIEWING", "review"], evaluated: ["EVALUATED", "approved"],
                     returned_for_correction: ["CORRECTIONS", "failed"], resubmission_requested: ["RESUBMIT", "failed"] };
const QSTATUS_OPTS = [["correct", "Correct"], ["partially_correct", "Partially correct"], ["incorrect", "Incorrect"],
                      ["not_attempted", "Not attempted"], ["needs_clarification", "Needs further review"]];
const Q_ATTEMPT_LABEL = { solved_confidently: "Solved confidently", attempted_unsure: "Attempted but unsure",
                          not_attempted: "Not attempted", doubt: "Needs doubt explanation" };
const DOUBT_STATUSES = ["attempted_unsure", "not_attempted", "doubt"];

function HwReviewWorkspace({ id, onBack, quick }) {
  const [d, setD] = useState(null);
  const [marks, setMarks] = useState({});
  const [form, setForm] = useState({ score_10: "", overall_feedback: "", main_mistakes: "",
                                     concepts_to_revise: "", recommended_practice: "", personal_message: "" });
  const [doubts, setDoubts] = useState({});
  const [docTab, setDocTab] = useState("attempted");
  const [full, setFull] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [wide, setWide] = useState(window.innerWidth >= 1100);
  const solRef = useRef(null);

  const load = useCallback(() => adminApi.get(`${HWD}/admin/submissions/${id}`).then(({ data }) => {
    setD(data);
    const m = {};
    (data.evaluation?.per_question || []).forEach((p) => { m[p.qno] = { qstatus: p.qstatus, comment: p.comment || "" }; });
    setMarks(m);
    if (data.evaluation) {
      setForm({ score_10: data.evaluation.score_10 ?? "", overall_feedback: data.evaluation.overall_feedback || "",
                main_mistakes: data.evaluation.main_mistakes || "", concepts_to_revise: data.evaluation.concepts_to_revise || "",
                recommended_practice: data.evaluation.recommended_practice || "", personal_message: data.evaluation.personal_message || "" });
    }
    const db2 = {};
    Object.entries(data.doubts || {}).forEach(([k, v]) => { db2[k] = { explanation: v.explanation || "", comment: v.comment || "" }; });
    setDoubts(db2);
  }).catch((e) => setErr(errText(e))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const fn = () => setWide(window.innerWidth >= 1100);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  if (!d) return <div className="admin-empty">Loading review workspace…</div>;
  const docs = d.documents || {};
  const doubtQs = (d.question_statuses || []).filter((q) => DOUBT_STATUSES.includes(q.status));
  const fileUrl = (slot) => `${API_URL}${HWD}/admin/files/${d.submission_id}/${slot}`;

  const act = async (action, opts = {}) => {
    setErr("");
    setMsg("");
    if (action === "publish" && form.score_10 === "") { setErr("Enter the final score out of 10 before publishing."); return; }
    if (action === "publish" && !window.confirm(`Publish evaluation with score ${form.score_10}/10? The student gets an email and this becomes part of their performance history.`)) return;
    try {
      await adminApi.post(`${HWD}/admin/submissions/${id}/evaluate`, {
        action, score_10: parseFloat(form.score_10) || 0, ...form,
        per_question: Object.entries(marks).map(([qno, m]) => ({ qno, ...m })),
      });
      setMsg({ save_draft: "Draft saved.", publish: "Evaluation published — student notified by email.",
               return_for_correction: "Returned for corrections.", request_resubmission: "Resubmission requested." }[action]);
      if (opts.advance && quick && action === "publish") { quick.onNext(); return; }
      load();
    } catch (e) { setErr(errText(e)); }
  };

  const aiSuggest = async () => {
    setErr(""); setMsg(""); setAiBusy(true);
    try {
      await adminApi.post(`${HWD}/admin/submissions/${id}/ai-suggest`);
      setMsg("AI is reading the student's work in the background — the draft appears here in about a minute.");
      const started = Date.now() / 1000;
      const poll = setInterval(async () => {
        try {
          const { data } = await adminApi.get(`${HWD}/admin/submissions/${id}`);
          if (data.ai_suggestion && data.ai_suggestion.generated_at >= started - 2) {
            clearInterval(poll);
            const s = data.ai_suggestion;
            setForm((f) => ({ ...f, score_10: s.score_10, overall_feedback: s.overall_feedback || f.overall_feedback,
                              main_mistakes: s.main_mistakes || f.main_mistakes,
                              concepts_to_revise: s.concepts_to_revise || f.concepts_to_revise }));
            setMarks((m) => {
              const next = { ...m };
              (s.per_question || []).forEach((p) => { next[p.qno] = { qstatus: p.qstatus, comment: p.comment }; });
              return next;
            });
            setMsg("AI draft applied — check it against the student's work, adjust anything, then publish. You remain the final grader.");
            setAiBusy(false);
          } else if (data.ai_suggestion_error && data.ai_suggestion_error.at >= started - 2) {
            clearInterval(poll);
            setErr(`AI suggestion failed: ${data.ai_suggestion_error.error}`);
            setAiBusy(false);
          } else if (Date.now() / 1000 - started > 180) {
            clearInterval(poll);
            setErr("AI is taking too long — please try again.");
            setAiBusy(false);
          }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) { setErr(errText(e)); setAiBusy(false); }
  };

  const saveDoubt = async (qno) => {
    try {
      await adminApi.post(`${HWD}/admin/submissions/${id}/doubts/${encodeURIComponent(qno)}`,
        { ...doubts[qno], addressed: true });
      setMsg(`Doubt ${qno} marked addressed.`);
      load();
    } catch (e) { setErr(errText(e)); }
  };

  const uploadTeacherSolution = async (files) => {
    if (!files?.length) return;
    try {
      const f = new FormData();
      f.append("file", files[0]);
      await adminApi.post(`${HWD}/admin/submissions/${id}/solution-doc`, f, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg("Teacher reference solution uploaded (visible to you only).");
      load();
    } catch (e) { setErr(errText(e)); }
  };

  const panel = (slot, label) => (
    <div key={slot} style={{ border: "1px solid #22314a", borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 420 }} data-testid={`hwd-panel-${slot}`}>
      <div className="admin-row" style={{ padding: "8px 10px", background: "#0c1727", justifyContent: "space-between" }}>
        <b style={{ color: "#c7d5e8", fontSize: 12 }}>{label}</b>
        <span>
          {docs[slot] && <button className="admin-btn" style={{ padding: "2px 8px", fontSize: 10 }} onClick={() => setFull(slot)} data-testid={`hwd-full-${slot}`}>Full screen</button>}
          {" "}
          {docs[slot] && <a className="admin-btn" style={{ padding: "2px 8px", fontSize: 10, textDecoration: "none" }} href={fileUrl(slot)} target="_blank" rel="noreferrer" data-testid={`hwd-dl-${slot}`}>Download</a>}
        </span>
      </div>
      {docs[slot] ? (
        <iframe src={fileUrl(slot)} title={label} style={{ flex: 1, minHeight: 420, border: "none", background: "#fff" }} data-testid={`hwd-frame-${slot}`} />
      ) : (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 30 }}>
          <p className="lead-sub" data-testid={`hwd-nodoc-${slot}`}>
            {slot === "solution" ? "No solution PDF has been provided." : "No document uploaded."}</p>
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="hw-review-workspace">
      <div className="admin-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <button className="admin-btn" onClick={onBack} data-testid="hwd-back">← All submissions</button>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {quick && <span className="status-pill review" data-testid="hwd-quick-pos">Quick grade — {quick.pos} of {quick.total}</span>}
          {quick && <button className="admin-btn" onClick={quick.onSkip} data-testid="hwd-quick-skip">Skip →</button>}
          <span className={`status-pill ${HWD_STATUS[d.status]?.[1] || "locked"}`} data-testid="hwd-ws-status">{HWD_STATUS[d.status]?.[0] || d.status}</span>
        </span>
      </div>
      <p className="lead-sub" style={{ marginTop: 8 }} data-testid="hwd-ws-meta">
        <b style={{ color: "#eef4fc" }}>{d.student_name}</b> · {d.category_label} · {d.subject} · {d.chapter} — {d.topic}{d.subtopic ? ` → ${d.subtopic}` : ""} · v{d.version} · submitted {fmtTs(d.submitted_at)}</p>
      {err && <p className="admin-error" data-testid="hwd-ws-error">{err}</p>}
      {msg && <p className="lead-sub" style={{ color: "#4ade80" }} data-testid="hwd-ws-msg">{msg}</p>}

      {!wide && (
        <div className="admin-row" style={{ gap: 6, margin: "10px 0" }} data-testid="hwd-doc-tabs">
          {[["question_paper", "Question Paper"], ["attempted", "Student Work"], ["solution", "Solution"]].map(([slot, l]) => (
            <button key={slot} className="admin-btn" style={docTab === slot ? { border: "1px solid #7dd3fc" } : {}}
                    onClick={() => setDocTab(slot)} data-testid={`hwd-doctab-${slot}`}>{l}{docs[slot] ? "" : " (none)"}</button>
          ))}
        </div>
      )}
      {wide ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.15fr 1fr", gap: 10, marginTop: 12 }} data-testid="hwd-three-panel">
          {panel("question_paper", "Question Paper")}
          {panel("attempted", "Student's Solved Homework")}
          {panel("solution", "Solution Reference")}
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>{panel(docTab, { question_paper: "Question Paper", attempted: "Student's Solved Homework", solution: "Solution Reference" }[docTab])}</div>
      )}
      {full && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 60, padding: 20 }} data-testid="hwd-fullscreen" onClick={() => setFull("")}>
          <iframe src={fileUrl(full)} title="full" style={{ width: "100%", height: "100%", border: "none", borderRadius: 8, background: "#fff" }} />
        </div>
      )}

      <div className="admin-card" style={{ marginTop: 14 }} data-testid="hwd-qreview">
        <h4>Question-wise review</h4>
        {(d.question_statuses || []).length === 0 && <p className="lead-sub">The student didn't track individual questions. Review the documents and enter the final score below.</p>}
        {(d.question_statuses || []).map((q, i) => (
          <div key={q.qno} style={{ borderTop: "1px solid #22314a", padding: "8px 0" }} data-testid={`hwd-rq-${i}`}>
            <div className="admin-row" style={{ flexWrap: "wrap", gap: 8 }}>
              <b style={{ color: "#eef4fc", minWidth: 44 }}>{q.qno}</b>
              <span className="status-pill review">{Q_ATTEMPT_LABEL[q.status]}</span>
              {q.note && <span className="lead-sub">student note: {q.note}</span>}
            </div>
            <div className="admin-row" style={{ flexWrap: "wrap", gap: 8, marginTop: 6 }}>
              <select className="admin-input" style={{ maxWidth: 200 }} value={marks[q.qno]?.qstatus || ""} data-testid={`hwd-rq-status-${i}`}
                      onChange={(e) => setMarks((m) => ({ ...m, [q.qno]: { ...m[q.qno], qstatus: e.target.value } }))}>
                <option value="">Your verdict…</option>
                {QSTATUS_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <input className="admin-input" style={{ flex: 1, minWidth: 200 }} placeholder="Comment, e.g. Correct formula, calculation error in step 2"
                     value={marks[q.qno]?.comment || ""} data-testid={`hwd-rq-comment-${i}`}
                     onChange={(e) => setMarks((m) => ({ ...m, [q.qno]: { ...m[q.qno], comment: e.target.value } }))} />
            </div>
          </div>
        ))}
      </div>

      {doubtQs.length > 0 && (
        <div className="admin-card" style={{ marginTop: 14, border: "1px solid #7dd3fc" }} data-testid="hwd-doubts">
          <h4 style={{ color: "#7dd3fc" }}>Questions Requiring Personal Attention</h4>
          <p className="lead-sub">Doubt handling here is separate from grading — addressing these never changes marks or doubt credits.</p>
          {doubtQs.map((q, i) => (
            <div key={q.qno} style={{ borderTop: "1px solid #22314a", padding: "8px 0" }} data-testid={`hwd-doubt-${i}`}>
              <div className="admin-row"><b style={{ color: "#eef4fc" }}>{q.qno}</b>
                <span className="status-pill review">{Q_ATTEMPT_LABEL[q.status]}</span>
                {(d.doubts || {})[q.qno]?.addressed && <span className="status-pill approved">ADDRESSED</span>}
              </div>
              <textarea className="admin-input" rows={2} placeholder="Your explanation for this question…" style={{ marginTop: 6 }}
                        value={doubts[q.qno]?.explanation || ""} data-testid={`hwd-doubt-exp-${i}`}
                        onChange={(e) => setDoubts((c) => ({ ...c, [q.qno]: { ...c[q.qno], explanation: e.target.value } }))} />
              <button className="admin-btn" style={{ marginTop: 6 }} onClick={() => saveDoubt(q.qno)} data-testid={`hwd-doubt-save-${i}`}>Mark addressed</button>
            </div>
          ))}
        </div>
      )}

      <div className="admin-card" style={{ marginTop: 14 }} data-testid="hwd-grading">
        <div className="admin-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <h4 style={{ margin: 0 }}>Final grading</h4>
          <button className="admin-btn" disabled={aiBusy || !docs.attempted} onClick={aiSuggest} data-testid="hwd-ai-suggest">
            {aiBusy ? "AI is reading the work…" : "AI suggest marks (draft)"}</button>
        </div>
        <div className="admin-row" style={{ flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <label className="lead-sub" style={{ fontSize: 13, color: "#eef4fc" }}>Final Homework Score</label>
          <input className="admin-input" type="number" min="0" max="10" step="0.5" style={{ maxWidth: 110 }}
                 value={form.score_10} data-testid="hwd-score"
                 onChange={(e) => setForm((f) => ({ ...f, score_10: e.target.value }))} />
          <span style={{ color: "#eef4fc" }}>/ 10</span>
          <input ref={solRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={(e) => uploadTeacherSolution(Array.from(e.target.files))} data-testid="hwd-teacher-sol-input" />
          <button className="admin-btn" onClick={() => solRef.current?.click()} data-testid="hwd-teacher-sol-btn">Upload teacher solution PDF</button>
        </div>
        {[["overall_feedback", "Overall feedback"], ["main_mistakes", "Main mistakes"], ["concepts_to_revise", "Concepts to revise"],
          ["recommended_practice", "Recommended practice"], ["personal_message", "Personal message from Pranjal Sir"]].map(([k, l]) => (
          <textarea key={k} className="admin-input" rows={2} placeholder={l} style={{ marginTop: 8 }}
                    value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`hwd-${k}`} />
        ))}
        <div className="admin-row" style={{ marginTop: 12, gap: 10, flexWrap: "wrap" }}>
          <button className="admin-btn" onClick={() => act("save_draft")} data-testid="hwd-save-draft">Save Draft</button>
          {quick ? (
            <button className="admin-btn" style={{ background: "#15803d" }} onClick={() => act("publish", { advance: true })} data-testid="hwd-quick-publish-next">Publish & Next →</button>
          ) : (
            <button className="admin-btn" style={{ background: "#15803d" }} onClick={() => act("publish")} data-testid="hwd-publish">Publish Evaluation</button>
          )}
          <button className="admin-btn" onClick={() => act("return_for_correction")} data-testid="hwd-return">Return for Correction</button>
          <button className="admin-btn" onClick={() => act("request_resubmission")} data-testid="hwd-resubmit">Request Resubmission</button>
        </div>
      </div>
    </div>
  );
}

function HwReviewDesk() {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [quick, setQuick] = useState(null);  // {queue: [ids], total}

  const load = useCallback(() => {
    adminApi.get(`${HWD}/admin/submissions`, { params: { category, status, search } })
      .then(({ data }) => { setRows(data.submissions); setStats(data.stats); })
      .catch(() => setRows([]));
  }, [category, status, search]);
  useEffect(() => { if (!openId) load(); }, [load, openId]);

  if (openId) {
    const quickProps = quick ? {
      pos: quick.total - quick.queue.length + 1, total: quick.total,
      onNext: () => {
        const rest = quick.queue.filter((x) => x !== openId);
        if (rest.length) { setQuick({ ...quick, queue: rest }); setOpenId(rest[0]); }
        else { setQuick(null); setOpenId(null); }
      },
      onSkip: () => {
        const i = quick.queue.indexOf(openId);
        const next = quick.queue[(i + 1) % quick.queue.length];
        if (next && next !== openId) setOpenId(next);
        else { setQuick(null); setOpenId(null); }
      },
    } : null;
    return <HwReviewWorkspace id={openId} onBack={() => { setOpenId(null); setQuick(null); }} quick={quickProps} />;
  }

  const pendingCount = (rows || []).filter((r) => ["submitted", "reviewing", "resubmission_requested"].includes(r.status)).length;

  return (
    <div data-testid="hw-review-desk">
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }} data-testid="hwd-stats">
          {[["New submissions", stats.new], ["Awaiting evaluation", stats.new + stats.reviewing], ["In progress", stats.reviewing],
            ["Evaluated", stats.evaluated], ["Returned for correction", stats.returned], [`Avg marks`, `${stats.avg_score_10}/10`]].map(([l, v], i) => (
            <div className="admin-card" key={l} style={{ textAlign: "center" }} data-testid={`hwd-stat-${i}`}>
              <b style={{ fontSize: 20, color: "#eef4fc" }}>{v}</b>
              <p className="lead-sub" style={{ margin: 0 }}>{l}</p>
            </div>
          ))}
        </div>
      )}
      <div className="admin-row" style={{ flexWrap: "wrap", marginTop: 14 }}>
        {pendingCount > 0 && (
          <button className="admin-btn" style={{ background: "#b45309", fontWeight: 600 }} data-testid="hwd-quick-start"
                  onClick={() => {
                    const q = (rows || []).filter((r) => ["submitted", "reviewing", "resubmission_requested"].includes(r.status))
                                          .map((r) => r.submission_id);
                    setQuick({ queue: q, total: q.length });
                    setOpenId(q[0]);
                  }}>
            Quick grade {pendingCount} pending →
          </button>
        )}
        <select className="admin-input" style={{ maxWidth: 160 }} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="hwd-filter-class">
          {CATS.map(([k, l]) => <option key={k} value={k}>{k ? l : "All classes"}</option>)}
        </select>
        <select className="admin-input" style={{ maxWidth: 190 }} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="hwd-filter-status">
          <option value="">All statuses</option>
          {Object.entries(HWD_STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input className="admin-input" style={{ maxWidth: 220 }} placeholder="Student name / title / ID" value={search}
               onChange={(e) => setSearch(e.target.value)} data-testid="hwd-filter-search" />
        <button className="admin-btn" onClick={load} data-testid="hwd-filter-apply"><RefreshCw size={13} /></button>
      </div>
      {rows === null && <div className="admin-empty">Loading…</div>}
      {rows && rows.length === 0 && (
        <div className="admin-empty" data-testid="hwd-empty">No homework submissions yet. Students submit from the Mentor app → Homework &amp; Personal Evaluation.</div>
      )}
      {rows && rows.map((r) => {
        const [label, cls] = HWD_STATUS[r.status] || [r.status, "locked"];
        return (
          <div className="admin-card" key={r.submission_id} style={{ marginTop: 10 }} data-testid={`hwd-row-${r.submission_id}`}>
            <div className="admin-row" style={{ flexWrap: "wrap", gap: 8 }}>
              <span className={`status-pill ${cls}`}>{label}</span>
              <b className="lead-name">{r.student_name}</b>
              <span className="lead-sub">{r.category_label} · {r.subject}</span>
              <span style={{ flex: 1, color: "#dbe6f4", fontSize: 13 }}>{r.chapter} — {r.topic}{r.subtopic ? ` → ${r.subtopic}` : ""}</span>
              {r.score_10 !== undefined && <b style={{ color: "#4ade80" }} data-testid={`hwd-row-score-${r.submission_id}`}>{r.score_10}/10</b>}
            </div>
            <div className="admin-row" style={{ flexWrap: "wrap", gap: 12, marginTop: 6 }}>
              <span className="lead-sub" data-testid={`hwd-row-qp-${r.submission_id}`}>Question PDF: {r.doc_flags.question_paper === "yes" ? "✓" : "✗"}</span>
              <span className="lead-sub" data-testid={`hwd-row-aw-${r.submission_id}`}>Attempted PDF: {r.doc_flags.attempted === "yes" ? "✓" : "✗"}</span>
              <span className="lead-sub" data-testid={`hwd-row-sol-${r.submission_id}`}>Solution PDF: {r.doc_flags.solution === "yes" ? "✓" : "optional, none"}</span>
              <span className="lead-sub">{r.summary.total} questions tracked · {r.summary.doubt} doubts</span>
              <span className="lead-sub">{fmtTs(r.submitted_at)}</span>
              <button className="admin-btn" style={{ marginLeft: "auto" }} onClick={() => setOpenId(r.submission_id)} data-testid={`hwd-open-${r.submission_id}`}>Open Review</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const TABS = [
  ["review", "Review Desk"], ["subs", "Legacy Submissions"], ["students", "Students"],
  ["payments", "Payments"], ["settings", "Settings"],
];

export default function AdminHomework() {
  const [tab, setTab] = useState("review");
  return (
    <div data-testid="admin-homework">
      <h4 style={{ marginBottom: 4 }}>Pranjal Sir's Homework Review Desk</h4>
      <span className="lead-sub">NOT AI. ONLY PRANJAL SIR. — Students upload their own question paper + handwritten work; every mark is entered by you. AI question tools live under the separate AI Test Bank tab.</span>
      <div className="admin-tabs" style={{ marginTop: 12, flexWrap: "wrap" }} data-testid="hw-admin-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} className={tab === k ? "active" : ""} onClick={() => setTab(k)} data-testid={`hw-tab-${k}`}>{label}</button>
        ))}
      </div>
      {tab === "review" && <HwReviewDesk />}
      {tab === "subs" && <HwSubmissions />}
      {tab === "students" && <HwStudents />}
      {tab === "payments" && <HwPayments />}
      {tab === "settings" && <HwSettings />}
    </div>
  );
}
